import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/services/utils/cn'

// Define the styles for ButtonSelect
const buttonSelectVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'border border-input bg-white text-black hover:bg-gray-100', // Default outline style
        active: 'border border-white bg-black text-white hover:bg-gray-700', // Active style
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonSelectVariants> {
  asChild?: boolean
  handleSelect?: (isActive: boolean) => void // Callback for when the button is selected
}

const ButtonSelect = React.forwardRef<HTMLButtonElement, ButtonSelectProps>(
  ({ className, size, asChild = false, handleSelect, ...props }, ref) => {
    // Manage active state
    const [isActive, setIsActive] = React.useState(false)

    // Toggle the active state on click
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (handleSelect) {
        handleSelect(!isActive)
      }
      setIsActive((prev) => !prev)
      if (props.onClick) props.onClick(e) // Call any passed onClick handler
    }

    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          buttonSelectVariants({
            variant: isActive ? 'active' : 'default',
            size,
            className,
          })
        )}
        ref={ref}
        onClick={handleClick}
        {...props}
      />
    )
  }
)

ButtonSelect.displayName = 'ButtonSelect'

interface ButtonCheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof buttonSelectVariants> {
  className?: string
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  children?: React.ReactNode
  disabled?: boolean
}

const ButtonCheckbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  ButtonCheckboxProps
>(({ className, size, ...props }, ref) => {
  return (
    <CheckboxPrimitive.Root
      className={cn(
        buttonSelectVariants({
          variant: props.checked ? 'active' : 'default',
          size,
          className,
        })
      )}
      disabled={props.disabled}
      checked={props.checked}
      ref={ref}
      {...props}
    />
  )
})

ButtonCheckbox.displayName = 'ButtonCheckbox'

export { ButtonSelect, buttonSelectVariants, ButtonCheckbox } // Use "default" for unselected, "active" for selected
